// Copyright 2026. WebPros International GmbH. All rights reserved.

import React, { FC } from 'react';
import { connect } from 'react-redux';
import { RootState } from 'admin/core/store';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import * as computeResourceActions from 'admin/computeResource/actions';
import {
    Alert,
    Button,
    FormField,
    Icon,
    List,
    Status,
    Text,
    Translate,
} from '@plesk/ui-library';
import { Loader } from 'common/components';
import { TruncateText } from 'common/components/TruncateText/TruncateText';
import CopyText from 'common/containers/CopyText/CopyText';
import ButtonWithConfirmation from 'common/components/ButtonWithConfirmation';
import { Tooltip } from 'common/components/Tooltip/Tooltip';
import { Dialog } from 'common/components/Dialog/Dialog';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import VGPUPlacements from 'admin/computeResource/page/components/VGPUPlacements/VGPUPlacements';
import GpuPassthroughAttachment from 'admin/computeResource/page/components/GpuPassthroughAttachment/GpuPassthroughAttachment';
import {
    DialogDivider,
    DialogSecondaryAction,
    HintText,
} from 'admin/computeResource/page/components/GpuTab/Styles';
import {
    GpuAllocationMode,
    IGpuCapability,
    IGpuResponse,
    IVGpuProfile,
    GpuStatus,
} from 'common/api/resources/Gpu';
import SelectInput from 'common/components/Select/SelectInput';
import { ISelectOption } from 'common/components/SelectInput';
import {
    INTENT_TYPE,
    SIZE,
} from 'common/constants';
import {
    convertToDataUnit,
    MiB,
    pickDataUnit,
} from 'common/helpers/units';

type GpuTabProps =
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

interface IProfileSelectOption extends ISelectOption {
    value: number;
    label: string;
    profile: IVGpuProfile;
}

const toProfileOption = (profile: IVGpuProfile): IProfileSelectOption => ({
    value: profile.id,
    label: profile.name,
    profile,
});

interface ITypeSelectOption extends ISelectOption {
    value: GpuAllocationMode;
    label: JSX.Element;
    isDisabled: boolean;
}

const UUID_MAX_LENGTH = 12;

const STATUS_TRANSLATION_KEYS: Record<GpuStatus, string> = {
    active: 'computeResource.vgpu.status.active',
    not_found: 'computeResource.vgpu.status.notFound',
    conflict: 'computeResource.vgpu.status.conflict',
};

const STATUS_INTENT_MAP: Record<GpuStatus, 'success' | 'warning' | 'danger'> = {
    active: 'success',
    not_found: 'danger',
    conflict: 'warning',
};

const ALLOCATION_MODE_TRANSLATION_KEYS: Record<GpuAllocationMode, string> = {
    vgpu: 'computeResource.vgpu.type.vgpu',
    passthrough: 'computeResource.vgpu.type.passthrough',
};

const ALLOCATION_MODE_UNAVAILABLE_KEYS: Record<GpuAllocationMode, string> = {
    vgpu: 'computeResource.vgpu.dialog.vgpuUnavailable',
    passthrough: 'computeResource.vgpu.dialog.passthroughUnavailable',
};

const ALLOCATION_MODES: GpuAllocationMode[] = ['vgpu', 'passthrough'];

const isPassthrough = (gpu: IGpuResponse) => gpu.allocation_mode === 'passthrough';

const isUnallocated = (gpu: IGpuResponse) => !gpu.allocation_mode && !gpu.is_partitioned;

const isIncompatible = (gpu: IGpuResponse) => {
    const capabilities = gpu.capabilities;

    return isUnallocated(gpu)
        && !!capabilities
        && !capabilities.vgpu.capable
        && !capabilities.passthrough.capable;
};

const allocationModeLabelKey = (gpu: IGpuResponse): string => {
    if (gpu.allocation_mode) {
        return ALLOCATION_MODE_TRANSLATION_KEYS[gpu.allocation_mode];
    }

    return gpu.is_partitioned
        ? 'computeResource.vgpu.type.allocated'
        : 'computeResource.vgpu.type.none';
};

const gpuUsage = (gpu: IGpuResponse): string | null => {
    if (isPassthrough(gpu)) {
        return `${gpu.passthrough_attachment?.server ? 1 : 0}/1`;
    }

    if (gpu.placement_addresses.length === 0) {
        return null;
    }

    return `${gpu.placement_addresses.filter(p => p.server).length}/${gpu.placement_addresses.length}`;
};

const formatVram = (vramMb: number): string => {
    const bytes = vramMb * MiB;
    const unit = pickDataUnit(bytes);

    return `${convertToDataUnit(bytes, unit)} ${unit}`;
};

const renderCapability = (capability?: IGpuCapability) => {
    if (!capability) {
        return null;
    }

    if (capability.capable) {
        return <Translate content="computeResource.vgpu.list.capable" />;
    }

    return (
        <Tooltip
            title={capability.reason ?? <Translate content="computeResource.vgpu.list.notCapable" />}
        >
            <HintText><Translate content="computeResource.vgpu.list.notCapableShort" /></HintText>
        </Tooltip>
    );
};

const hasAttachedServers = (gpu: IGpuResponse) => gpu.placement_addresses.some(p => p.server)
    || Boolean(gpu.passthrough_attachment?.server);

const isTypeAvailable = (gpu: IGpuResponse | null, type: GpuAllocationMode, fallback: boolean) => {
    const capability = gpu?.capabilities?.[type];

    return capability ? capability.capable : fallback;
};

const defaultAllocationMode = (gpu: IGpuResponse): GpuAllocationMode =>
    !isTypeAvailable(gpu, 'vgpu', true) && isTypeAvailable(gpu, 'passthrough', false)
        ? 'passthrough'
        : 'vgpu';

const GpuTab: FC<GpuTabProps> = function ({
    computeResource,
    gpus,
    isLoading,
    isPartitioning,
    isUnpartitioning,
    isRefreshing,
    computeResourceActions: {
        getGpus,
        partitionGpu,
        unpartitionGpu,
        refreshGpus,
    },
}) {
    const [selectedGpu, setSelectedGpu] = React.useState<IGpuResponse | null>(null);
    const [selectedProfile, setSelectedProfile] = React.useState<IProfileSelectOption | null>(null);
    const [selectedType, setSelectedType] = React.useState<GpuAllocationMode>('vgpu');

    const openDialog = React.useCallback((gpu: IGpuResponse) => {
        const currentProfile = gpu.available_profiles.find(profile => profile.name === gpu.current_profile);

        setSelectedGpu(gpu);
        setSelectedProfile(currentProfile ? toProfileOption(currentProfile) : null);
        setSelectedType(gpu.allocation_mode || defaultAllocationMode(gpu));
    }, []);

    const closeDialog = React.useCallback(() => {
        setSelectedGpu(null);
        setSelectedProfile(null);
        setSelectedType('vgpu');
    }, []);

    const handleApply = React.useCallback(async () => {
        if (!selectedGpu) {
            return;
        }

        if (selectedType === 'passthrough') {
            try {
                await partitionGpu(selectedGpu.id, { type: 'passthrough' });
                closeDialog();
            } catch {
                // Form errors are handled by the global error handler.
            }

            return;
        }

        if (!selectedProfile) {
            return;
        }

        try {
            await partitionGpu(selectedGpu.id, { profile_id: selectedProfile.value });
            closeDialog();
        } catch {
            // Form errors are handled by the global error handler.
        }
    }, [selectedGpu, selectedProfile, selectedType, partitionGpu, closeDialog]);

    const handleUnpartition = React.useCallback(async () => {
        if (!selectedGpu?.allocation_mode) {
            return;
        }

        try {
            await unpartitionGpu(selectedGpu.id, selectedGpu.allocation_mode);
            closeDialog();
        } catch {
            // Form errors are handled by the global error handler.
        }
    }, [selectedGpu, unpartitionGpu, closeDialog]);

    React.useEffect(() => {
        computeResource.id && computeResource.gpu_driver_version && getGpus(computeResource.id);
    }, [getGpus, computeResource.id, computeResource.gpu_driver_version]);

    const columns = React.useMemo(() => [
        {
            key: 'colInd',
            title: <Translate content="computeResource.vgpu.list.index" />,
        },
        {
            key: 'colName',
            title: <Translate content="computeResource.vgpu.list.name" />,
        },
        {
            key: 'colVram',
            title: <Translate content="computeResource.vgpu.list.vram" />,
        },
        {
            key: 'colPciAddress',
            title: <Translate content="computeResource.vgpu.list.pciAddress" />,
        },
        {
            key: 'colStatus',
            title: <Translate content="computeResource.vgpu.list.status" />,
        },
        {
            key: 'colUuid',
            title: <Translate content="computeResource.vgpu.list.uuid" />,
        },
        {
            key: 'colPassthroughCapable',
            title: (
                <Tooltip title={<Translate content="computeResource.vgpu.list.passthroughHint" />}>
                    <HintText><Translate content="computeResource.vgpu.list.passthrough" /></HintText>
                </Tooltip>
            ),
            align: 'center' as const,
        },
        {
            key: 'colVgpuCapable',
            title: (
                <Tooltip title={<Translate content="computeResource.vgpu.list.vgpuHint" />}>
                    <HintText><Translate content="computeResource.vgpu.list.vgpu" /></HintText>
                </Tooltip>
            ),
            align: 'center' as const,
        },
        {
            key: 'colAllocationMode',
            title: <Translate content="computeResource.vgpu.list.allocationMode" />,
        },
        {
            key: 'colVGpuUsage',
            title: <Translate content="computeResource.vgpu.list.vGpuUsage" />,
            align: 'center' as const,
        },
        {
            key: 'colActions',
            title: '',
            width: '1%',
        },
    ], []);

    const data = React.useMemo(() => gpus.map((item, ind) => {
        const hasVms = hasAttachedServers(item);
        const isNotActive = item.status !== 'active';
        const isIncompatibleCard = isIncompatible(item);
        const isDisabled = item.is_processing || hasVms || isNotActive || isIncompatibleCard;

        let tooltipKey = 'computeResource.vgpu.tooltip.partition';
        if (item.is_processing) {
            tooltipKey = 'computeResource.vgpu.tooltip.disabledProcessing';
        } else if (hasVms) {
            tooltipKey = 'computeResource.vgpu.tooltip.disabledHasVms';
        } else if (isNotActive) {
            tooltipKey = 'computeResource.vgpu.tooltip.disabledNotActive';
        } else if (isIncompatibleCard) {
            tooltipKey = 'computeResource.vgpu.tooltip.disabledIncompatible';
        }

        return {
            id: item.id,
            colInd: ind + 1,
            colName: item.name,
            colPciAddress: <CopyText>{item.pci_address}</CopyText>,
            colUuid: (
                <CopyText text={item.uuid}>
                    <TruncateText text={item.uuid} maxLength={UUID_MAX_LENGTH} />
                </CopyText>
            ),
            colAllocationMode: (
                <>
                    <div>
                        {isUnallocated(item) ? (
                            <Text intent={INTENT_TYPE.MUTED}><Translate content={allocationModeLabelKey(item)} /></Text>
                        ) : (
                            <Translate content={allocationModeLabelKey(item)} />
                        )}
                    </div>
                    {item.current_profile && (
                        <Text intent={INTENT_TYPE.MUTED} fontSize={SIZE.SM}>{item.current_profile}</Text>
                    )}
                </>
            ),
            colVram: item.vram_mb === null ? null : formatVram(item.vram_mb),
            colPassthroughCapable: renderCapability(item.capabilities?.passthrough),
            colVgpuCapable: renderCapability(item.capabilities?.vgpu),
            colVGpuUsage: gpuUsage(item),
            colStatus: (
                <Status intent={STATUS_INTENT_MAP[item.status]}>
                    <Translate content={STATUS_TRANSLATION_KEYS[item.status]} />
                </Status>
            ),
            colActions: isDisabled ? (
                <Tooltip title={<Translate content={tooltipKey} />}>
                    <div>
                        <Button
                            ghost={true}
                            icon={<Icon name={'pencil'} />}
                            disabled={true}
                        />
                    </div>
                </Tooltip>
            ) : (
                <Button
                    ghost={true}
                    icon={<Icon name={'pencil'} />}
                    onClick={() => openDialog(item)}
                />
            ),
            gpu: item,
        };
    }), [gpus, openDialog]);

    const profileOptions: IProfileSelectOption[] = React.useMemo(() => {
        if (!selectedGpu) {
            return [];
        }

        return selectedGpu.available_profiles.map(toProfileOption);
    }, [selectedGpu]);

    const typeOptions: ITypeSelectOption[] = React.useMemo(() => ALLOCATION_MODES.map(type => {
        const isAvailable = isTypeAvailable(selectedGpu, type, type === 'vgpu');

        return {
            value: type,
            label: isAvailable ? (
                <Translate content={ALLOCATION_MODE_TRANSLATION_KEYS[type]} />
            ) : (
                <>
                    <Translate content={ALLOCATION_MODE_TRANSLATION_KEYS[type]} />
                    {' - '}
                    {selectedGpu?.capabilities?.[type]?.reason
                        ?? <Translate content={ALLOCATION_MODE_UNAVAILABLE_KEYS[type]} />}
                </>
            ),
            isDisabled: !isAvailable,
        };
    }), [selectedGpu]);

    const lockedMode: GpuAllocationMode | null = selectedGpu?.allocation_mode ?? null;
    const isProfileUnchanged = lockedMode === 'vgpu' && selectedProfile?.profile.name === selectedGpu?.current_profile;

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                    {computeResource.gpu_driver_version && (
                        <Translate
                            content="computeResource.vgpu.driverVersion"
                            params={{ version: computeResource.gpu_driver_version }}
                        />
                    )}
                </div>

                <Button
                    ghost={true}
                    state={isRefreshing ? 'loading' : undefined}
                    onClick={() => computeResource.id && refreshGpus(computeResource.id)}
                >
                    <Translate content="computeResource.vgpu.refreshBtn" />
                </Button>
            </div>

            <Loader isLoading={isLoading}>
                <List
                    columns={columns}
                    data={data}
                    renderRowBody={(row) => {
                        const { gpu } = row;
                        const hasConflicts = gpu.status === 'conflict'
                            && gpu.conflict_details !== null
                            && gpu.conflict_details.length > 0;
                        const hasPlacements = gpu.placement_addresses.length > 0;

                        return (
                            <>
                                {hasConflicts && (
                                    <div style={{ marginBottom: hasPlacements || isPassthrough(gpu) ? 15 : 0 }}>
                                        <Alert intent={INTENT_TYPE.WARNING}>
                                            {gpu.conflict_details!.map((detail, i) => (
                                                <div key={i}>
                                                    <Translate
                                                        content={`computeResource.vgpu.conflict.${detail.key}`}
                                                        params={detail.params}
                                                    />
                                                </div>
                                            ))}
                                        </Alert>
                                    </div>
                                )}
                                {isPassthrough(gpu) ? (
                                    <GpuPassthroughAttachment gpu={gpu} />
                                ) : hasPlacements
                                    ? <VGPUPlacements gpu={gpu} />
                                    : !hasConflicts && (
                                        <Translate content="computeResource.vgpu.noPlacements" />
                                    )
                                }
                            </>
                        );
                    }}
                    rowKey={'id'}
                    emptyView={
                        <Alert><Translate content="computeResource.vgpu.emptyView" /></Alert>
                    }
                />
            </Loader>

            <Dialog
                isOpen={selectedGpu !== null}
                closeHandler={closeDialog}
                size={'sm'}
                heading={<Translate content="computeResource.vgpu.dialog.title" />}
            >
                {lockedMode === null && (
                    <FormField
                        name="allocation_mode"
                        label={<Translate content="computeResource.vgpu.dialog.selectType" />}
                        vertical
                    >
                        {({ getId }) => (
                            <SelectInput<ITypeSelectOption>
                                inputId={getId()}
                                menuPosition="fixed"
                                options={typeOptions}
                                isClearable={false}
                                isSearchable={false}
                                value={typeOptions.find(option => option.value === selectedType) ?? null}
                                onChange={(option) => setSelectedType((option as ITypeSelectOption).value)}
                            />
                        )}
                    </FormField>
                )}

                {lockedMode !== null && (
                    <Alert intent={INTENT_TYPE.INFO}>
                        <Translate content="computeResource.vgpu.dialog.modeLockedHint" />
                    </Alert>
                )}

                {selectedType === 'vgpu' && (
                    <FormField
                        name="profile_id"
                        label={<Translate content="computeResource.vgpu.dialog.selectProfile" />}
                        vertical
                    >
                        {({ getId }) => (
                            <SelectInput<IProfileSelectOption>
                                inputId={getId()}
                                menuPosition="fixed"
                                options={profileOptions}
                                isClearable={false}
                                value={selectedProfile}
                                onChange={(option) => setSelectedProfile(option as IProfileSelectOption)}
                                placeholder={<Translate content="computeResource.vgpu.dialog.selectPlaceholder" />}
                            />
                        )}
                    </FormField>
                )}

                {selectedType === 'vgpu' && selectedProfile && (
                    <table style={{ width: '100%', marginBottom: 15 }}>
                        <tbody>
                            <tr>
                                <th style={{ padding: 5, textAlign: 'left' }}><Translate content="computeResource.vgpu.dialog.maxInstances" /></th>
                                <td style={{ padding: 5 }}>{selectedProfile.profile.max_instances}</td>
                            </tr>
                            <tr>
                                <th style={{ padding: 5, textAlign: 'left' }}><Translate content="computeResource.vgpu.dialog.frameRateLimit" /></th>
                                <td style={{ padding: 5 }}>{selectedProfile.profile.frame_rate_limit_fps} fps</td>
                            </tr>
                            <tr>
                                <th style={{ padding: 5, textAlign: 'left' }}><Translate content="computeResource.vgpu.dialog.frameBufferMemory" /></th>
                                <td style={{ padding: 5 }}>{selectedProfile.profile.frame_buffer_memory_mb} MB</td>
                            </tr>
                            <tr>
                                <th style={{ padding: 5, textAlign: 'left' }}><Translate content="computeResource.vgpu.dialog.gridLicense" /></th>
                                <td style={{ padding: 5 }}>{selectedProfile.profile.grid_license}</td>
                            </tr>
                        </tbody>
                    </table>
                )}

                {selectedType === 'passthrough' && lockedMode === null && (
                    <Alert intent={INTENT_TYPE.INFO}>
                        <Translate content="computeResource.vgpu.dialog.passthroughHint" />
                    </Alert>
                )}

                {lockedMode !== 'passthrough' && (
                    <Button
                        intent={INTENT_TYPE.PRIMARY}
                        size={SIZE.LG}
                        fill={true}
                        disabled={(selectedType === 'vgpu' && !selectedProfile) || isProfileUnchanged || isUnpartitioning}
                        state={isPartitioning ? 'loading' : undefined}
                        onClick={handleApply}
                    >
                        <Translate content="computeResource.vgpu.dialog.applyBtn" />
                    </Button>
                )}

                {lockedMode !== null && (
                    <>
                        <DialogDivider />
                        <DialogSecondaryAction>
                            <ButtonWithConfirmation
                                isLoading={isUnpartitioning}
                                disabled={isPartitioning}
                                icon={<Icon name={'cross-mark'} />}
                                handleConfirm={handleUnpartition}
                                confirmationButtonText={<Translate content="computeResource.vgpu.unallocateBtn" />}
                                confirmationButtonGhost={false}
                                buttonIntent={INTENT_TYPE.DANGER}
                                buttonSize={SIZE.LG}
                                translations={{
                                    title: <Translate content="computeResource.vgpu.unallocateBtn" />,
                                    text: <Translate content="computeResource.vgpu.unallocateConfirmation" />,
                                    button: <Translate content="computeResource.vgpu.unallocateBtn" />,
                                    tooltip: <Translate content="computeResource.vgpu.tooltip.unallocate" />,
                                }}
                            />
                        </DialogSecondaryAction>
                    </>
                )}
            </Dialog>
        </>
    );
};

const mapStateToProps = (state: RootState) => ({
    computeResource: state.computeResource.item,
    gpus: state.computeResource.gpus,
    isLoading: state.app.loadingFlags.has(LOADING_FLAGS.COMPUTE_RESOURCE_GPUS),
    isPartitioning: state.app.loadingFlags.has(LOADING_FLAGS.PARTITION_GPU),
    isUnpartitioning: state.app.loadingFlags.has(LOADING_FLAGS.UNPARTITION_GPU),
    isRefreshing: state.app.loadingFlags.has(LOADING_FLAGS.REFRESH_COMPUTE_RESOURCE_GPUS),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    computeResourceActions: bindActionCreators(computeResourceActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(GpuTab);
